// Visual flag for high/urgent jobs across every surface that displays
// a job: calendar pills (Month/Week/Day → CalendarJobPill), the
// needs-scheduling tray rows in FieldHub, the JobDrawer side panel,
// and JobDetail's header.
//
// Two variants, same semantics:
//   corner — a small CSS triangle anchored to the top-right corner of
//            its parent. Used inside CalendarJobPill at month density
//            where space is tight and status owns the block's color
//            identity (the corner adds without competing). Parent must
//            be position: relative for the absolute positioning to anchor.
//
//   chip   — a small token-colored chip (icon + label). Used in the
//            tray rows, drawer header, and detail header where there's
//            room for a labeled affordance and where it sits in a chip
//            cluster next to the status chip.
//
// Tiering decision (recon-driven — both 'high' and 'urgent' exist in
// the priority CHECK + in real data on dev):
//   urgent → strong: var(--danger) family, AlertTriangle, larger corner
//   high   → softer: var(--warning) family, AlertCircle, smaller corner
//   low / normal → null (no surface affordance)
//
// All colors are tokens so the indicator participates in the theme
// system (light / sepia / high-contrast variants all defined).

import { AlertTriangle, AlertCircle } from 'lucide-react'

type Priority = 'low' | 'normal' | 'high' | 'urgent' | string | null | undefined

const TIER: Record<'urgent' | 'high', { label: string; color: string; bg: string; border: string; Icon: typeof AlertTriangle }> = {
  urgent: { label: 'Urgent', color: 'var(--danger)',  bg: 'var(--danger-dim)',  border: 'var(--danger-border)',  Icon: AlertTriangle },
  high:   { label: 'High',   color: 'var(--warning)', bg: 'var(--warning-dim)', border: 'var(--warning-border)', Icon: AlertCircle },
}

function tierFor(priority: Priority): 'urgent' | 'high' | null {
  if (priority === 'urgent') return 'urgent'
  if (priority === 'high') return 'high'
  return null
}

// ─── Corner variant ────────────────────────────────────────────────
// CSS triangle technique: a zero-size div with two adjacent border
// colors. The pill's overflow:hidden keeps the triangle from poking
// past the rounded corner, so the appearance is a clean wedge.
//
// Sizes:
//   urgent: 12px wedge — most prominent
//   high:   10px wedge — softer
export function JobPriorityCorner({ priority }: { priority: Priority }) {
  const tier = tierFor(priority)
  if (!tier) return null
  const size = tier === 'urgent' ? 12 : 10
  return (
    <div aria-label={`${TIER[tier].label} priority`}
      style={{
        position: 'absolute', top: 0, right: 0,
        width: 0, height: 0,
        borderTop: `${size}px solid ${TIER[tier].color}`,
        borderLeft: `${size}px solid transparent`,
        pointerEvents: 'none',
      }} />
  )
}

// ─── Chip variant ──────────────────────────────────────────────────
// Sizes:
//   sm — tray rows, drawer header (matches the existing status chip size)
//   md — detail header (slightly larger to balance with the page H1)
export function JobPriorityChip({ priority, size = 'sm' }: { priority: Priority; size?: 'sm' | 'md' }) {
  const tier = tierFor(priority)
  if (!tier) return null
  const t = TIER[tier]
  const px = size === 'md' ? 11 : 10
  const padY = size === 'md' ? 3 : 2
  const padX = size === 'md' ? 9 : 7
  const iconSize = size === 'md' ? 12 : 10
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: `${padY}px ${padX}px`, borderRadius: 100,
      background: t.bg, color: t.color, border: `1px solid ${t.border}`,
      fontSize: px, fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      <t.Icon style={{ width: iconSize, height: iconSize }} />
      {t.label}
    </span>
  )
}
